import type { ErrorResponse } from '@apollo/client/link/error'
import { addBreadcrumb, captureException } from '@sentry/react'
import type { GraphQLError, GraphQLFormattedError } from 'graphql'
import { get } from 'lodash-es'

import ErrorProvider from '../errorHandler/ErrorProvider'
import { getStatusCode, isForbiddenError, suppressesErrorReport, suppressesErrorToast } from './links.helpers'

export const graphqlErrorLink = ({ response, graphQLErrors, operation }: ErrorResponse) => {
  if (graphQLErrors?.length) {
    // @ts-expect-error - Cypress is a global variable
    const isCypress = window.Cypress
    const isPlaywright = navigator.webdriver
    // 401 Unauthorized errors are expected when the user is logged out, so we don't want to show them in Sentry
    const isForbiddenErr = Boolean(graphQLErrors.some((error) => isForbiddenError(error, 'graphql')))
    if (!isForbiddenErr && !isCypress && !isPlaywright && !suppressesErrorReport(operation)) {
      const graphQLError = graphQLErrors[0]
      const statusCode = getStatusCode(graphQLError, 'graphql')

      // This is used to track certain BE errors, such as timeout in blockchain network from Oracle, or errors from microservices.
      // The graphQLErrors[0].originalError can either have id if from Oracle, or a response if from a microservice.
      const originalError = (graphQLError as any).originalError
      const exceptionID = originalError?.id ?? originalError?.response?.headers?.['x-request-id'] ?? null

      // Add a breadcrumb to track that an error alert was shown to the user, 
      // providing context for UI actions/console errors without creating a new issue in Sentry.
      addBreadcrumb({
        category: 'Error alert',
        message: `Error alert displayed to user (or in console): ${graphQLErrors}`,
        level: 'info',
        data: exceptionID ? { exception_id: exceptionID } : undefined,
      })

      // Capture the error in Sentry with additional context
      captureException(graphQLError, {
        contexts: {
          graphql: {
            operation: operation.operationName,
            variables: JSON.stringify(operation.variables, null, 2),
          },
        },
        tags: {
          ...(statusCode && { status_code: statusCode }),
          ...(exceptionID && { exceptionID: exceptionID }),
        },
        /* 
        This fingerprint will:
        - Group errors by operation name AND message
        - Create separate Sentry issues for different error messages (even within the same operation)
        - Still group identical error messages within the same operation 
        */
        fingerprint: [
          'graphql-error',
          operation.operationName || 'unknown_operation',
          graphQLError.message || 'no_message'
        ],
      })
    }

    // Log the error to the console for debugging purposes
    console.error(operation)
    console.error(graphQLErrors)

    // Check if any error has retry_with_base64 flag - if so, don't show it (it will be retried automatically)
    const hasRetryWithBase64Flag = graphQLErrors.some(
      (error) =>
        error?.extensions?.retry_with_base64 ||
        (error as any)?.originalError?.response?.data?.retry_with_base64
    )

    // Show errors to the user via ErrorProvider (unless it's a retry error, or the operation shows
    // its own message in context)
    if (!hasRetryWithBase64Flag && !suppressesErrorToast(operation)) {
      ErrorProvider.showErrors(graphQLErrors as Readonly<GraphQLError>[])
    }

    // Mark error as handled
    if (response?.errors && response?.data) {
      // If we have data, we can remove the errors that are related to the data.
      // This allows us to still show the user the data that we have, even if some of the data is missing.
      response.errors = response.errors.reduce((acc, curr) => {
        if (!get(response.data, curr.path!.slice(0, -1))) {
          acc.push(curr)
        }
        return acc
      }, [] as GraphQLFormattedError[])
    }
  }
}